// REACT
import { useState } from 'react';

// YARN
import { ThemeProvider } from '@mui/material/styles';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'react-toastify';

// SERVICES
import { deleteCultureGroup, deleteStructureNonReturnCultureGroup } from 'services/API/Cooperative';
import { displayErrorMessage } from 'utils/tools_functions';
import { useTranslation } from 'react-i18next';

// INTERFACES
import { ICultureGroup } from 'components/generics/Interface/ICultureGroup';

// THEMES
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { deleteSectorNonReturnCultureGroup } from 'services/API/Secteur';
import { deleteExploitationNonReturnCultureGroup } from 'services/API/Exploitation';

const useCultureGroupAction = (
  ownerId: number,
  cultureGroup: ICultureGroup[],
  refreshData: Function,
  setModal: Function,
  setEditData: Function,
  groupType: 'mixedFarmingCultureGroup' | 'nonReturnCultureGroup',
  from: 'cooperatives' | 'sectors' | 'exploitations',
) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);

  const { t } = useTranslation();

  const handleDeleteDialogOpen = async (id: number) => {
    setActionId(id);
    setOpenDeleteDialog(true);
  };

  const handleEditDialogOpen = async (id: number) => {
    setActionId(id);
    updateCultureGroupFromAction(id);
    setModal(true);
  };

  const updateCultureGroupFromAction = (groupId: number) => {
    const cultureGroupToUpdate = cultureGroup?.find((group) => group?.id === groupId);

    setEditData(cultureGroupToUpdate);
  };

  const handleDeleteClick = async () => {
    setOpenDeleteDialog(false);
    const cultureGroupToDelete = cultureGroup.find((group) => group.id === actionId);
    try {
      if (groupType === 'mixedFarmingCultureGroup') {
        await deleteCultureGroup(ownerId, cultureGroupToDelete?.id);
      }
      if (groupType === 'nonReturnCultureGroup') {
        switch (from) {
          case 'cooperatives':
            await deleteStructureNonReturnCultureGroup(ownerId, {
              groupId: cultureGroupToDelete?.id,
            });
            break;
          case 'sectors':
            await deleteSectorNonReturnCultureGroup(ownerId, { groupId: cultureGroupToDelete?.id });
            break;
          case 'exploitations':
            await deleteExploitationNonReturnCultureGroup(ownerId, {
              groupId: cultureGroupToDelete?.id,
            });
            break;
        }
      }
      toast.success(t('success.cultureGroupDeleted', { name: cultureGroupToDelete?.name }));
      refreshData();
    } catch (error) {
      toast.error(displayErrorMessage('ERR_DELETE_CULTURE_GROUP'));
    }
  };

  const renderDeleteDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="xs" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            {t('dialog.confirmDeleteQuestion')} <br /> {t('dialog.deleteGroupQuestion')}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return {
    renderDeleteDialog,
    handleDeleteDialogOpen,
    handleEditDialogOpen,
  };
};

export default useCultureGroupAction;
